import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { appointmentApi, patientApi, taskApi, formAssignmentApi } from '../../lib/api';
import KudosWidget from '../../components/kudos/KudosWidget';
import api from '../../lib/api';
import { updateTaskResilient } from '../../lib/offlineQueue';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  ClipboardList, Activity, Users, CheckCircle,
  ArrowRight, Calendar as CalendarIcon, FileText, AlertTriangle
} from 'lucide-react';
import WorkedCasesChart from './WorkedCasesChart';
import FormsInProgressWidget from '../../components/dashboard/FormsInProgressWidget';
import MyWeekStrip from '../../components/dashboard/MyWeekStrip';
import IncidentsInboxWidget from '../../components/dashboard/IncidentsInboxWidget';

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

export default function NurseDashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [myPatients, setMyPatients] = useState([]);
  const [upcomingAppts, setUpcomingAppts] = useState([]);
  const [upcomingForms, setUpcomingForms] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadDashboardData(); }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const [tasksRes, apptsRes, formsRes, activityRes] = await Promise.all([
        taskApi.list().catch(() => ({ data: { tasks: [] } })),
        appointmentApi.list({ date: todayStr }).catch(() => ({ data: { appointments: [] } })),
        formAssignmentApi.list({ status: 'pending', due_within_days: 7 }).catch(() => ({ data: { assignments: [] } })),
        api.get('/auth/activity?limit=100').catch(() => ({ data: { logs: [] } })),
      ]);
      setTasks(tasksRes.data.tasks || []);
      setUpcomingAppts(apptsRes.data.appointments || []);
      setUpcomingForms(formsRes.data.assignments || []);
      setActivity(activityRes.data.logs || []);
      // Derive "my patients" from assigned tasks + today's appts
      const patientIds = new Set();
      (tasksRes.data.tasks || []).forEach(t => { if (t.patient_id) patientIds.add(t.patient_id); });
      (apptsRes.data.appointments || []).forEach(a => { if (a.patient_id) patientIds.add(a.patient_id); });
      if (patientIds.size > 0) {
        // Fetch a page of patients and filter locally (dashboards don't need pagination)
        const pRes = await patientApi.list({ limit: 500 }).catch(() => ({ data: [] }));
        const all = Array.isArray(pRes.data) ? pRes.data : (pRes.data.patients || []);
        setMyPatients(all.filter(p => patientIds.has(p.id)));
      } else {
        setMyPatients([]);
      }
    } catch (err) {
      console.error('Nurse dashboard load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const completeTask = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await updateTaskResilient(api, taskId, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch { /* ignore */ }
  };

  const pendingTasks = useMemo(() =>
    tasks.filter(t => t.status !== 'completed').sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3)),
    [tasks]
  );
  const completedToday = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return tasks.filter(t => t.status === 'completed' && (t.updated_at || t.created_at || '').slice(0, 10) === today);
  }, [tasks]);

  // Activity logs drive the WorkedCasesChart drill-down
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="nurse-dashboard-loading">
        <div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const urgent = pendingTasks.filter(t => t.priority === 'high').length;

  return (
    <div className="space-y-8" data-testid="nurse-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('dashboard.myWorkload')}
        </h1>
        <p className="text-[#64748B] mt-1">
          {t('dashboard.workloadSummary', { name: user?.name?.split(' ')[0] || 'there', pending: pendingTasks.length, completed: completedToday.length })}
        </p>
      </div>

      {/* Open patient incidents (only renders if there are unresolved reports) */}
      <IncidentsInboxWidget />

      {/* Peer recognition / Employee of the Week */}
      <KudosWidget />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="careiq-metric-card cursor-pointer hover:shadow-lg transition-shadow" data-testid="stat-urgent-tasks">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#991B1B]/10 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-[#991B1B]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{urgent}</div>
                <div className="text-xs text-[#64748B]">{t('dashboard.urgent')}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="careiq-metric-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/tasks')} data-testid="stat-all-tasks">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0E7490]/10 rounded-lg flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-[#0E7490]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{pendingTasks.length}</div>
                <div className="text-xs text-[#64748B]">{t('dashboard.tasksCount')}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="careiq-metric-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/patients')} data-testid="stat-my-patients">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#1A56DB]/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[#1A56DB]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{myPatients.length}</div>
                <div className="text-xs text-[#64748B]">{t('dashboard.myPatients')}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="careiq-metric-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/appointments')} data-testid="stat-appts">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#CA8A04]/10 rounded-lg flex items-center justify-center">
                <CalendarIcon className="w-5 h-5 text-[#CA8A04]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{upcomingAppts.length}</div>
                <div className="text-xs text-[#64748B]">Today's Appts</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="careiq-metric-card" data-testid="stat-completed">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#166534]/10 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-[#166534]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{completedToday.length}</div>
                <div className="text-xs text-[#64748B]">{t('dashboard.completedToday')}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My Week — 7-day overview of tasks + appointments */}
      <MyWeekStrip
        role="nurse"
        onSelectDate={(d) => navigate(`/tasks?date=${d}`)}
      />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Forms In Progress */}
        <FormsInProgressWidget />
        {/* Pending Tasks */}
        <Card className="careiq-card" data-testid="pending-tasks-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A2342]">{t('dashboard.pendingTasks')}</CardTitle>
            <Link to="/tasks" className="text-sm text-[#0E7490] hover:underline flex items-center gap-1">
              {t('common.viewAll')} <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {pendingTasks.length === 0 ? (
              <div className="text-center py-8 text-sm text-[#94A3B8]">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                All caught up — no pending tasks.
              </div>
            ) : (
              <div className="space-y-3">
                {pendingTasks.slice(0, 8).map((task) => (
                  <div
                    key={task.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                      task.priority === 'high'
                        ? 'border-[#991B1B]/40 bg-[#991B1B]/5'
                        : 'border-[#E2E8F0] hover:bg-[#F8FAFC]'
                    }`}
                    data-testid={`task-row-${task.id}`}
                  >
                    <Checkbox
                      checked={task.status === 'completed'}
                      onCheckedChange={() => completeTask(task.id, task.status)}
                      className="mt-1"
                      data-testid={`task-checkbox-${task.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#0A2342] text-sm truncate">{task.title}</div>
                      <div className="text-xs text-[#64748B] truncate">
                        {task.patient_name ? `Patient: ${task.patient_name}` : (task.form_name || 'No patient linked')}
                        {task.due_date && <span> · Due {new Date(task.due_date).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${
                      task.priority === 'high' ? 'bg-[#991B1B] text-white' :
                      task.priority === 'medium' ? 'bg-[#92400E] text-white' :
                      'bg-[#64748B] text-white'
                    }`}>
                      {task.priority || 'medium'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Form Submissions */}
        <Card className="careiq-card" data-testid="upcoming-forms-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A2342]">Upcoming Forms</CardTitle>
            <Link to="/forms" className="text-sm text-[#0E7490] hover:underline flex items-center gap-1">
              {t('common.viewAll')} <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {upcomingForms.length === 0 ? (
              <div className="text-center py-8 text-sm text-[#94A3B8]">
                <FileText className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                No forms due in the next 7 days.
              </div>
            ) : (
              <div className="space-y-2">
                {upcomingForms.slice(0, 8).map((a) => {
                  const due = a.due_date ? new Date(a.due_date) : null;
                  const isOverdue = due && due < new Date();
                  return (
                    <Link
                      key={a.id}
                      to={a.form_id ? `/forms/${a.form_id}/fill?patientId=${a.patient_id || ''}` : '/forms'}
                      className="flex items-center justify-between p-2 rounded border border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors"
                      data-testid={`upcoming-form-${a.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[#0A2342] truncate">{a.form_name || 'Form'}</div>
                        <div className="text-[10px] text-[#64748B] truncate">{a.patient_name || 'Unassigned patient'}</div>
                      </div>
                      <div className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${
                        isOverdue ? 'bg-[#991B1B]/10 text-[#991B1B]' : 'bg-[#0E7490]/10 text-[#0E7490]'
                      }`}>
                        {due ? (isOverdue ? 'Overdue' : `Due ${due.toLocaleDateString()}`) : 'No due date'}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Today's Appointments */}
        <Card className="careiq-card lg:col-span-1" data-testid="today-appts-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A2342]">{t('dashboard.todaysAppts')}</CardTitle>
            <Link to="/appointments" className="text-sm text-[#0E7490] hover:underline flex items-center gap-1">
              {t('common.viewAll')} <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {upcomingAppts.length === 0 ? (
              <div className="text-center py-6 text-sm text-[#94A3B8]">
                <CalendarIcon className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                No appointments today.
              </div>
            ) : (
              <div className="space-y-2">
                {upcomingAppts.slice(0, 6).map((appt) => (
                  <div key={appt.id} className="flex items-center gap-3 p-2 rounded border border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors">
                    <div className="text-center min-w-[48px]">
                      <div className="text-sm font-medium text-[#0A2342]">{appt.scheduled_time}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/patients/${appt.patient_id}`} className="font-medium text-[#0A2342] text-sm hover:text-[#0E7490] truncate block">
                        {appt.patient_name}
                      </Link>
                      <div className="text-[10px] text-[#64748B]">{(appt.appointment_type || 'visit').replace('_', ' ')}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timeline Chart: worked cases over 7 days — interactive drill-down */}
        <div className="lg:col-span-2" data-testid="timeline-chart-card">
          <WorkedCasesChart activity={activity} title="Worked Cases — Last 7 Days" height={240} testId="nurse-worked-cases-chart" />
        </div>
      </div>

      {/* My Patients */}
      {myPatients.length > 0 && (
        <Card className="careiq-card" data-testid="my-patients-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A2342]">{t('dashboard.myPatients')}</CardTitle>
            <span className="text-xs text-[#64748B]">{myPatients.length} patient(s) assigned via tasks or appointments</span>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {myPatients.slice(0, 9).map((p) => (
                <Link
                  key={p.id}
                  to={`/patients/${p.id}`}
                  className="p-3 rounded border border-[#E2E8F0] hover:bg-[#F8FAFC] hover:border-[#0E7490] transition-colors flex items-center gap-3"
                  data-testid={`my-patient-${p.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-[#0E7490]/10 flex items-center justify-center text-[#0E7490] text-xs font-semibold">
                    {(p.first_name?.[0] || '') + (p.last_name?.[0] || '')}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#0A2342] truncate">{p.first_name} {p.last_name}</div>
                    <div className="text-[10px] text-[#64748B]">MRN {p.mrn || '—'}</div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
